import React, { useCallback, useContext, type FC } from 'react';
import type { ConfigurableMenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useConfigurableMessageAndIconMap } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import {
	MenuButtonItem,
	type MenuButtonItemProps,
	MenuLinkItem,
} from '@atlassian/navigation-system';
import { PeekMenuIdContext } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import { convertToConfigurableMenuId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/convert-to-configurable-menu-id/index.tsx';
import { useSectionItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { SECTION_ITEM_MAP } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { USER_NAVIGATION_ITEM_LINKS } from '../../../../common/constants/index.tsx';

type MenuItemProps = { menuId: ConfigurableMenuId } & Omit<
	MenuButtonItemProps,
	'children' | 'iconBefore'
>;

// remove on blu-6576_migrate_to_platform_customisation cleanup
export const MenuItem: FC<MenuItemProps> = ({ menuId, ...props }) => {
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(menuId);

	// This is part of an intermediate state workaround where links are accounted for;
	// we don't expect links to be in the More menu at the end state,
	// at which point this and the MenuLinkItem below can be removed.
	const url = USER_NAVIGATION_ITEM_LINKS[menuId];

	return (
		<>
			{url ? (
				<MenuLinkItem {...props} elemBefore={icon} href={url}>
					{formattedMessage}
				</MenuLinkItem>
			) : (
				<MenuButtonItem {...props} elemBefore={icon}>
					{formattedMessage}
				</MenuButtonItem>
			)}
		</>
	);
};

export type MoreMenuItemComponentProps = {
	// eslint-disable-next-line jira/react/handler-naming
	closeMenu: () => void;
	menuId: string;
	key: string;
};

export function MenuItemComponent({ menuId, closeMenu, key }: MoreMenuItemComponentProps) {
	const { setPeekMenuId } = useContext(PeekMenuIdContext);

	const configurableId = convertToConfigurableMenuId(menuId);

	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(configurableId);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	// This is part of an intermediate state workaround where links are accounted for;
	// we don't expect links to be in the More menu at the end state,
	// at which point this and the MenuLinkItem below can be removed.
	const url = USER_NAVIGATION_ITEM_LINKS[menuId];

	const onClick = useCallback(() => {
		closeMenu();

		setPeekMenuId(configurableId);

		sendSectionItemAnalyticsEvent({
			menuId: configurableId,
			sectionItem: SECTION_ITEM_MAP[configurableId],
		});
	}, [closeMenu, configurableId, sendSectionItemAnalyticsEvent, setPeekMenuId]);

	return (
		<>
			{url ? (
				<MenuLinkItem elemBefore={icon} href={url} onClick={onClick} key={key}>
					{formattedMessage}
				</MenuLinkItem>
			) : (
				<MenuButtonItem elemBefore={icon} onClick={onClick} key={key}>
					{formattedMessage}
				</MenuButtonItem>
			)}
		</>
	);
}
